import React from "react";
import { Helmet } from "react-helmet";
import "./terms.css";

const RefundsAndCancellation = () => {
  return (
    <div className="terms-container">
      <Helmet>
        <title>Refunds & Cancellation | QuanTech (Canada)</title>
        <meta
          name="description"
          content="Read QuanTech's refund and cancellation policy for website development and digital services in Canada."
        />
        <meta property="og:title" content="Refunds & Cancellation | QuanTech (Canada)" />
        <meta
          property="og:description"
          content="Understand how refunds and cancellations are handled at QuanTech (Canada)."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.quantech.pro/refunds-and-cancellation"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Refunds & Cancellation | QuanTech (Canada)" />
        <meta
          name="twitter:description"
          content="Understand how refunds and cancellations are handled at QuanTech (Canada)."
        />
      </Helmet>

      <center>
        <h1
          style={{
            textDecoration: "underline",
            textDecorationColor: "#b9ff66",
          }}
        >
          Refunds & Cancellation Policy
        </h1>
      </center>

      <br />
      <br />

      <p>
        At <strong>QuanTech</strong>, we aim to be fair and transparent. This policy
        explains how cancellations and refunds are handled for website development
        and digital services provided to Canadian clients. All amounts below are in{" "}
        <strong>Canadian dollars (CAD)</strong>.
      </p>

      <h2>Consumer rights & applicable law</h2>
      <p>
        This policy is intended to comply with applicable federal and provincial laws
        in Canada (including privacy rules such as PIPEDA and provincial consumer
        protection statutes). If you are a consumer (an individual buying services
        for personal, non-business use), you may have additional rights under your
        province’s consumer protection legislation which will apply despite any
        conflicting terms below.
      </p>

      <h2>Cancellation before work commences</h2>
      <p>
        If the Client cancels before any billable work has commenced, QuanTech will
        generally issue a full refund of amounts paid by the Client, less any
        non-refundable third-party costs (for example domain registration, hosting,
        or third-party licensing fees) that QuanTech has already paid on the
        Client’s behalf.
      </p>

      <h2>Partial refunds once work has commenced</h2>
      <p>
        If work has begun, refunds (if any) will be calculated fairly and
        proportionately based on the stage of work completed, documented hours and
        expenses incurred, and non-recoverable third-party costs. Prior to issuing a
        refund, QuanTech will provide the Client an itemized statement of completed
        work and deductions.
      </p>

      <h2>Post-delivery revision window & final acceptance</h2>
      <p>
        After final delivery, the Client has a <strong>7-day revision window</strong>
        for minor corrections (e.g., text edits, contact details). Minor corrections
        do not include structural redesign, new features, major content rework or
        functionality changes—those are treated as additional work and billed
        separately.
      </p>

      <h2>Refunds after delivery</h2>
      <p>
        Refunds after delivery are not automatic. QuanTech will consider requests for
        refunds for major non-conformities (e.g., deliverable materially differs from
        the agreed scope or contains defects we cannot reasonably remedy). Where a
        consumer has statutory rights under provincial law (for example certain
        cancellation or refund rights for internet agreements), those statutory rights
        will apply.
      </p>

      <h2>Additional work & scope changes</h2>
      <p>
        Work requested beyond the original scope is treated as additional work. For
        additional work, QuanTech normally requires full payment for that quoted
        work in advance. Additional-work payments are generally non-refundable once
        the extra work is completed and delivered, subject to any consumer rights
        that may apply under applicable law.
      </p>

      <h2>Non-refundable costs</h2>
      <p>
        Third-party costs such as domain registration, SSL certificates, plugin
        licenses, templates, and hosting fees are non-refundable (these are payments
        made to third parties and not under QuanTech’s control). QuanTech will
        provide receipts or invoices for such third-party purchases upon request.
      </p>

      <h2>Service suspension for non-payment</h2>
      <p>
        If payment obligations are not met, QuanTech may suspend hosting, access,
        or other Services until outstanding invoices are paid. QuanTech will provide
        reasonable notice before permanent removal of data; Clients remain responsible
        for maintaining their own backups. Suspension is not a cancellation for the
        purposes of this refund policy.
      </p>

      <h2>Refund processing time</h2>
      <p>
        If a refund is approved, QuanTech will initiate the refund to the original
        payment method within <strong>7 business days</strong>. The time for the
        Client’s payment provider (bank or card issuer) to post the refund may take
        additional time (commonly 3–14 business days depending on the card issuer or
        payment method). If you have not received a refund after 15 business days,
        please contact hello@quantech.pro.
      </p>

      <h2>Taxes & fees</h2>
      <p>
        All invoices will include applicable federal and provincial sales taxes where
        required (e.g., GST/HST). Any refunds will be adjusted to reflect taxes that
        were originally collected and remitted.
      </p>

      <h2>How to request a cancellation or refund</h2>
      <p>
        To request cancellation or refund, contact us at&nbsp;
        <a
          style={{ textDecorationColor: "#b9ff66", color: "black" }}
          href="mailto:hello@quantech.pro"
        >
          hello@quantech.pro
        </a>
        . Provide your invoice number, a brief reason, and any supporting
        documentation. We will acknowledge receipt of your request and aim to
        respond promptly.
      </p>

      <h2>Dispute resolution</h2>
      <p>
        We encourage Clients to contact us first so we can try to resolve disputes
        informally. If a dispute cannot be resolved, consumers may be entitled to
        bring claims under applicable provincial consumer protection legislation or
        to other statutory dispute resolution mechanisms.
      </p>

      <h2>Policy changes</h2>
      <p>
        QuanTech may update this policy from time to time; updated versions will be
        posted on our website with the effective date. Where required by law, we will
        notify existing Clients of material changes.
      </p>

      <h2>Acceptance</h2>
      <p>
        By paying an invoice or accepting Services from QuanTech you acknowledge
        that you have read and agreed to this Refunds & Cancellation Policy. This
        policy does not limit any non-waivable statutory rights you may have under
        applicable Canadian or provincial law.
      </p>

      <h2>Need help?</h2>
      <p>
        For questions about refunds or cancellations contact us at&nbsp;
        <a
          style={{ textDecorationColor: "#b9ff66", color: "black" }}
          href="mailto:hello@quantech.pro"
        >
          hello@quantech.pro
        </a>
        .
      </p>
    </div>
  );
};

export default RefundsAndCancellation;